import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { isDarkBackground } from './isDarkBackground';

type Props = {
  /**
   * Flip the wash from ink to white. Leave this unset to have the skeleton
   * work it out from the surface it was mounted on, which is what makes it
   * self-tint on beige, green and purple slides alike. Pass an explicit value
   * when the surface can't be read from the DOM (an image background, a canvas)
   * or when you already know the answer.
   */
  dark?: boolean;
  /**
   * Corner rounding. A number is read as px; pass a string to match a slide
   * element's own rounding, which is stored as a percentage.
   */
  radius?: number | string;
  /**
   * Draw the float shadow that frames the reserved area. Turn this off when the
   * skeleton sits inside something that already has its own frame (a thumbnail
   * cell, a bordered card).
   */
  framed?: boolean;
  /** Accessible label (announced to screen readers; no visible text). */
  label?: string;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Placeholder for a slide image that hasn't loaded yet. Fills its container
 * completely — whatever aspect ratio the slot has, the skeleton takes it — and
 * washes the slide's own background with a low-opacity sweep rather than
 * painting a color of its own, so it never fights the slide's palette.
 *
 * Ported from the "Image Loader Explorations" design doc (round 3 — 2c tinted
 * by the slide itself). The parent must establish the size; this renders at
 * 100% × 100%.
 */
const ImageSkeleton: React.FC<Props> = ({
  dark,
  radius = 10,
  framed = true,
  label = 'Loading image',
  className,
  style,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const detected = useSurfaceIsDark(ref, dark === undefined);
  const isDark = dark ?? detected;

  return (
    <Frame
      ref={ref}
      role="status"
      aria-label={label}
      aria-live="polite"
      className={className}
      style={style}
      $dark={isDark}
      $radius={radius}
      $framed={framed}
    >
      <Wash aria-hidden="true" $dark={isDark} />
    </Frame>
  );
};

/**
 * Walks up to the first ancestor that actually paints a background and reports
 * whether it is dark. Runs once on mount — slide backgrounds don't change while
 * an image is loading — and stays `false` on the server, which matches the
 * beige the app renders on by default.
 */
const useSurfaceIsDark = (
  ref: React.RefObject<HTMLElement>,
  enabled: boolean,
): boolean => {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    if (!enabled || !ref.current) return;

    let node: HTMLElement | null = ref.current.parentElement;
    while (node) {
      const { backgroundColor } = window.getComputedStyle(node);
      if (backgroundColor && !isTransparent(backgroundColor)) {
        setDark(isDarkBackground(backgroundColor));
        return;
      }
      node = node.parentElement;
    }
  }, [enabled, ref]);

  return dark;
};

const isTransparent = (color: string) =>
  color === 'transparent' || /^rgba\(.*,\s*0\s*\)$/.test(color.replace(/\s+/g, ' '));

/* Sweeps the highlight across the block, right to left. */
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const Frame = styled.div<{
  $dark: boolean;
  $radius: number | string;
  $framed: boolean;
}>`
  width: 100%;
  height: 100%;
  border-radius: ${({ $radius }) =>
    typeof $radius === 'number' ? `${$radius}px` : $radius};
  overflow: hidden;
  ${({ $framed, $dark }) =>
    $framed &&
    css`
      box-shadow: ${$dark
        ? '0 0 0 1px rgba(255, 255, 255, 0.1)'
        : '0 2px 20px #deaf8d55'};
    `}
`;

const Wash = styled.div<{ $dark: boolean }>`
  width: 100%;
  height: 100%;
  /* A wash of ink (or light) over whatever the slide background is — the
     skeleton never carries a color of its own. */
  background: ${({ $dark }) =>
    $dark
      ? 'linear-gradient(100deg, rgba(255, 255, 255, 0.06) 30%, rgba(255, 255, 255, 0.16) 50%, rgba(255, 255, 255, 0.06) 70%)'
      : 'linear-gradient(100deg, rgba(40, 47, 51, 0.05) 30%, rgba(40, 47, 51, 0.13) 50%, rgba(40, 47, 51, 0.05) 70%)'};
  background-size: 220% 100%;
  animation: ${shimmer} 1.9s cubic-bezier(0.645, 0.045, 0.355, 1) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background-position: 50% 0;
  }
`;

export default ImageSkeleton;
